<template>
  <div class="server-management-container">
    <div class="card">
      <div class="card-header">
        <h3>Server Management</h3>
        <p class="subtitle">
          Manage server cache, configurations, and user tokens
        </p>
      </div>

      <div class="card-body">
        <!-- User Blacklist Section -->
        <div class="action-section">
          <div class="section-header">
            <h4>User Token Management</h4>
            <i
              class="fas fa-question-circle help-icon"
              @click="toggleHelp"
              title="Show help"
            ></i>
          </div>
          <div class="blacklist-form">
            <div class="form-group">
              <label for="usernameInput">Username or User ID:</label>
              <input
                id="usernameInput"
                v-model="usernameToBlacklist"
                type="text"
                placeholder="Enter username or user ID"
                :disabled="isBlacklisting"
                @keyup.enter="blacklistUserTokens"
                class="username-input"
              />
            </div>
            <button
              @click="blacklistUserTokens"
              :disabled="isBlacklisting || !usernameToBlacklist.trim()"
              class="blacklist-btn danger"
            >
              <span v-if="isBlacklisting" class="spinner"></span>
              {{ isBlacklisting ? 'Blacklisting...' : 'Blacklist User Tokens' }}
            </button>
            <p class="help-text">
              This will invalidate all active tokens for the specified user and
              force them to log in again. Use "{ all }" to blacklist ALL user
              tokens.
            </p>
          </div>
        </div>

        <!-- Force Password Change Section -->
        <div class="action-section">
          <div class="section-header">
            <h4>Force Password Reset</h4>
            <i
              class="fas fa-question-circle help-icon"
              @click="toggleHelp"
              title="Show help"
            ></i>
          </div>
          <div class="password-reset-form">
            <div class="form-group">
              <label for="passwordResetInput">Username:</label>
              <input
                id="passwordResetInput"
                v-model="usernameToReset"
                type="text"
                placeholder="Enter username"
                :disabled="isForcingReset"
                @keyup.enter="forcePasswordReset"
                class="username-input"
              />
            </div>
            <button
              @click="forcePasswordReset"
              :disabled="isForcingReset || !usernameToReset.trim()"
              class="reset-btn warning"
            >
              <span v-if="isForcingReset" class="spinner"></span>
              {{ isForcingReset ? 'Setting Reset...' : 'Force Password Reset' }}
            </button>
            <p class="help-text">
              This will require the user to change their password on next login
              and immediately log them out
            </p>
          </div>
        </div>

        <!-- Manual Refresh Section -->
        <div class="action-section">
          <h4>Server Cache Refresh</h4>
          <button
            @click="refreshServer"
            :disabled="isRefreshing"
            class="refresh-btn primary"
          >
            <span v-if="isRefreshing" class="spinner"></span>
            {{ isRefreshing ? 'Refreshing...' : 'Refresh Server Cache' }}
          </button>
          <p class="help-text">
            Manually reload token blacklist and other server configurations
          </p>
        </div>

        <!-- Compact sticky status (auto-clears; does not permanently grow the page) -->
        <div v-if="lastOperationResult" class="status-section">
          <div
            :class="[
              'status-message',
              lastOperationResult.success ? 'success' : 'error',
            ]"
          >
            <div class="status-icon">
              {{ lastOperationResult.success ? '✅' : '❌' }}
            </div>
            <div class="status-content">
              <strong>{{
                lastOperationResult.success ? 'Success' : 'Error'
              }}</strong>
              <p>{{ lastOperationResult.message }}</p>
              <small
                v-if="operationDetails && operationDetails.length"
                class="status-detail-line"
              >
                {{ formatOperationDetailsLine }}
              </small>
            </div>
            <button
              type="button"
              class="status-dismiss"
              title="Dismiss"
              @click="clearOperationStatus"
            >
              ×
            </button>
          </div>
        </div>

        <!-- Help Section -->
        <div v-if="showHelp" class="help-section">
          <div class="help-header">
            <h4>When to use these features:</h4>
            <span class="close-hint" @click="toggleHelp">click to close</span>
          </div>
          <ul class="help-list">
            <li>
              <strong>Token Blacklisting:</strong> When a user account is
              compromised or needs immediate logout
            </li>
            <li>
              <strong>Blacklist All Users:</strong> Type <code>{ all }</code> to
              log out everyone (emergency use only)
            </li>
            <li>
              <strong>Force Password Reset:</strong> When a user needs to change
              their password for security reasons
            </li>
            <li>
              <strong>Server Refresh:</strong> After using
              <code>manageUsers.js</code> command line tools
            </li>
            <li>When token validation seems inconsistent</li>
            <li>
              After manual database changes to <code>active_tokens</code> table
            </li>
            <li>To force reload of server configurations</li>
          </ul>
        </div>

        <!-- Tab Bar for Log Sections -->
        <div class="log-tabs">
          <button
            class="log-tab"
            :class="{ active: logTab === 'activity' }"
            @click="logTab = 'activity'"
          >
            <i class="fas fa-list"></i> Activity Log
          </button>
          <button
            class="log-tab"
            :class="{ active: logTab === 'logins' }"
            @click="
              logTab = 'logins';
              if (!loginAnalysis) fetchLoginAnalysis();
            "
          >
            <i class="fas fa-shield-alt"></i> Failed Logins
          </button>
          <button
            class="log-tab"
            :class="{ active: logTab === 'users' }"
            @click="
              logTab = 'users';
              if (!adminUsers.length) fetchAdminUsers();
            "
          >
            <i class="fas fa-users"></i> Users
          </button>
        </div>

        <!-- Users Section -->
        <div class="users-section" v-show="logTab === 'users'">
          <div class="section-header">
            <h4>User Accounts</h4>
            <div class="analysis-controls">
              <input
                ref="userFilterInput"
                v-model="userSearchTerm"
                type="text"
                class="username-input user-filter-input"
                placeholder="Filter users..."
                title="Filter users (Esc: clear filter & reset sort)"
                @keydown.esc.prevent="resetUserTableView"
              />
              <button
                @click="fetchAdminUsers"
                :disabled="isLoadingUsers"
                class="log-action-btn refresh-logs-btn"
                title="Refresh users"
              >
                <span v-if="isLoadingUsers" class="spinner"></span>
                <span v-else><i class="fas fa-sync-alt"></i></span>
              </button>
            </div>
          </div>

          <div v-if="usersError" class="error-message">
            <div class="error-icon">&#x26A0;&#xFE0F;</div>
            <div class="error-text">{{ usersError }}</div>
          </div>

          <div class="analysis-table-wrap users-table-wrap">
            <table class="analysis-table users-table" v-if="filteredAdminUsers.length">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Username</th>
                  <th>Display Name</th>
                  <th>Role</th>
                  <th>Phone</th>
                  <th title="Password changed">Pwd Chg</th>
                  <th
                    class="sortable-th"
                    :class="{
                      sorted: userSortField === 'lastAccessed',
                    }"
                    title="Click to sort by last accessed (Esc resets)"
                    @click="toggleUserSortLastAccessed"
                  >
                    Last Accessed
                    <i
                      v-if="userSortField === 'lastAccessed'"
                      class="fas"
                      :class="
                        userSortDir === 'desc'
                          ? 'fa-sort-down'
                          : 'fa-sort-up'
                      "
                    ></i>
                    <i v-else class="fas fa-sort sort-idle"></i>
                  </th>
                  <th>Expires</th>
                  <th>Status</th>
                  <th style="text-align: center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="user in filteredAdminUsers"
                  :key="user.id"
                  :class="{ 'you-row': user.you }"
                >
                  <td>{{ user.id }}</td>
                  <td>
                    <strong>{{ user.username }}</strong>
                    <span v-if="user.you" class="you-badge">you</span>
                  </td>
                  <td>{{ user.displayName || '—' }}</td>
                  <td>{{ user.role }}</td>
                  <td>{{ user.phone || '—' }}</td>
                  <td class="date-cell">
                    {{ formatUserDate(user.passwordChanged) }}
                  </td>
                  <td
                    class="date-cell"
                    :title="lastAccessedTitle(user)"
                  >
                    {{ formatUserDateTime(user.lastAccessed) }}
                  </td>
                  <td class="date-cell">
                    {{ formatUserDate(user.expires) }}
                    <span
                      v-if="user.daysUntilExpires != null"
                      class="days-hint"
                    >
                      ({{ formatDaysUntil(user.daysUntilExpires) }})
                    </span>
                  </td>
                  <td>
                    <span
                      class="user-status-badge"
                      :class="'status-' + user.accountStatus"
                    >
                      {{ formatAccountStatus(user.accountStatus) }}
                    </span>
                  </td>
                  <td class="actions-cell">
                    <div class="user-actions">
                      <button
                        class="user-action-btn extend"
                        :disabled="
                          extendingUsername === user.username ||
                          resettingUsername === user.username
                        "
                        title="Extend account expiry by 180 days from max(today, current expiry)"
                        @click="extendUserExpiry(user)"
                      >
                        <span
                          v-if="extendingUsername === user.username"
                          class="spinner"
                        ></span>
                        <template v-else>
                          <i class="fas fa-calendar-plus"></i>
                          +180d
                        </template>
                      </button>
                      <button
                        class="user-action-btn warning"
                        :disabled="
                          isForcingReset ||
                          resettingUsername === user.username ||
                          extendingUsername === user.username ||
                          user.passwordResetRequired
                        "
                        :title="
                          user.passwordResetRequired
                            ? 'Password reset already required'
                            : 'Require password change on next login and log out'
                        "
                        @click="forcePasswordResetForUser(user)"
                      >
                        <span
                          v-if="resettingUsername === user.username"
                          class="spinner"
                        ></span>
                        <template v-else>
                          <i class="fas fa-key"></i>
                          {{
                            user.passwordResetRequired
                              ? 'Reset Pending'
                              : 'reset'
                          }}
                        </template>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <p v-else-if="!isLoadingUsers && !usersError" class="no-data">
              No users found
            </p>
            <p v-else-if="isLoadingUsers" class="no-data">Loading users…</p>
          </div>
        </div>

        <!-- Failed Login Analysis Section -->
        <div class="login-analysis-section" v-show="logTab === 'logins'">
          <div class="section-header">
            <h4>Failed Login Analysis</h4>
            <div class="analysis-controls">
              <select
                v-model.number="loginAnalysisDays"
                class="days-select"
                @change="fetchLoginAnalysis"
              >
                <option :value="1">Last 24h</option>
                <option :value="7">Last 7 days</option>
                <option :value="30">Last 30 days</option>
                <option :value="90">Last 90 days</option>
              </select>
              <button
                @click="fetchLoginAnalysis"
                :disabled="isLoadingAnalysis"
                class="log-action-btn refresh-logs-btn"
                title="Refresh analysis"
              >
                <span v-if="isLoadingAnalysis" class="spinner"></span>
                <span v-else><i class="fas fa-sync-alt"></i></span>
              </button>
            </div>
          </div>

          <div v-if="loginAnalysisError" class="error-message">
            <div class="error-icon">&#x26A0;&#xFE0F;</div>
            <div class="error-text">{{ loginAnalysisError }}</div>
          </div>

          <div v-if="loginAnalysis" class="analysis-grid">
            <!-- Status Summary -->
            <div class="analysis-card">
              <h5>Login Summary</h5>
              <div class="summary-stats">
                <div
                  v-for="s in loginAnalysis.byStatus"
                  :key="s.status"
                  class="stat-row"
                >
                  <span class="stat-badge" :class="'stat-' + s.status">{{
                    s.status
                  }}</span>
                  <span class="stat-count">{{ s.count }}</span>
                </div>
              </div>
            </div>

            <!-- Active Lockouts -->
            <div
              v-if="
                loginAnalysis.activeLockouts &&
                loginAnalysis.activeLockouts.length
              "
              class="analysis-card lockouts-card"
            >
              <h5>
                Active Lockouts
                <span class="badge-count">{{
                  loginAnalysis.activeLockouts.filter((l) => l.isActive).length
                }}</span>
              </h5>
              <div class="lockout-list">
                <div
                  v-for="l in loginAnalysis.activeLockouts"
                  :key="l.ip"
                  class="lockout-row"
                  :class="{ active: l.isActive }"
                >
                  <span class="lockout-ip">{{ l.ip }}</span>
                  <span class="lockout-fails">{{ l.failCount }} fails</span>
                  <span class="lockout-until">{{
                    l.isActive
                      ? 'until ' + formatTime(l.lockedUntil)
                      : 'expired'
                  }}</span>
                </div>
              </div>
            </div>

            <!-- Top Offending IPs -->
            <div class="analysis-card">
              <h5>Top Failing IPs</h5>
              <div class="analysis-table-wrap">
                <table
                  class="analysis-table"
                  v-if="loginAnalysis.byIp && loginAnalysis.byIp.length"
                >
                  <thead>
                    <tr>
                      <th>IP Address</th>
                      <th style="text-align: center">Fails</th>
                      <th>Usernames Tried</th>
                      <th>Last Attempt</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in loginAnalysis.byIp" :key="row.ipAddr">
                      <td class="ip-cell">{{ row.ipAddr }}</td>
                      <td
                        class="count-cell"
                        :class="{ 'high-count': row.failCount >= 10 }"
                      >
                        {{ row.failCount }}
                      </td>
                      <td class="usernames-cell" :title="row.usernames">
                        {{ truncate(row.usernames, 40) }}
                      </td>
                      <td class="date-cell">
                        {{ formatDateTime(row.lastAttempt) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <p v-else class="no-data">No failed login attempts</p>
              </div>
            </div>

            <!-- Top Failing Usernames -->
            <div class="analysis-card">
              <h5>Top Failing Usernames</h5>
              <div class="analysis-table-wrap">
                <table
                  class="analysis-table"
                  v-if="
                    loginAnalysis.byUsername && loginAnalysis.byUsername.length
                  "
                >
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th style="text-align: center">Fails</th>
                      <th>IP Addresses</th>
                      <th>Last Attempt</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in loginAnalysis.byUsername"
                      :key="row.username"
                    >
                      <td>{{ row.username }}</td>
                      <td
                        class="count-cell"
                        :class="{ 'high-count': row.failCount >= 10 }"
                      >
                        {{ row.failCount }}
                      </td>
                      <td class="ip-cell" :title="row.ipAddresses">
                        {{ truncate(row.ipAddresses, 40) }}
                      </td>
                      <td class="date-cell">
                        {{ formatDateTime(row.lastAttempt) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <p v-else class="no-data">No failed login attempts</p>
              </div>
            </div>

            <!-- Daily Trend -->
            <div class="analysis-card full-width">
              <h5>Daily Trend</h5>
              <div class="analysis-table-wrap">
                <table
                  class="analysis-table"
                  v-if="loginAnalysis.byDay && loginAnalysis.byDay.length"
                >
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th style="text-align: center">Failed</th>
                      <th style="text-align: center">Success</th>
                      <th>Fail Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in loginAnalysis.byDay" :key="row.day">
                      <td>{{ formatDate(row.day) }}</td>
                      <td
                        class="count-cell"
                        :class="{ 'high-count': row.failed >= 10 }"
                      >
                        {{ row.failed }}
                      </td>
                      <td class="count-cell success-count">
                        {{ row.success }}
                      </td>
                      <td>
                        <div class="fail-bar-container">
                          <div
                            class="fail-bar"
                            :style="{ width: getFailRate(row) + '%' }"
                            :class="{ 'high-rate': getFailRate(row) > 50 }"
                          ></div>
                          <span class="fail-rate-text"
                            >{{ getFailRate(row) }}%</span
                          >
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <p v-else class="no-data">No login data</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Activity Log Section -->
        <div class="activity-log-section" v-show="logTab === 'activity'">
          <h4>Recent Activity Log</h4>
          <div class="log-search-wrapper">
            <div class="search-input-container">
              <button
                @click="clearSearch"
                class="clear-search-btn"
                title="Clear search"
              >
                ×
              </button>
              <input
                v-model="activitySearchTerm"
                type="text"
                :placeholder="
                  useBackendSearch
                    ? 'Search all (Enter). Use commas to refine: fred,tenant'
                    : 'Search loaded logs... (use ! to exclude)'
                "
                class="log-search-input"
                @input="!useBackendSearch && filterActivityLogs()"
                @keyup.enter="useBackendSearch && searchBackend()"
              />
            </div>
            <button
              @click="toggleSearchMode"
              class="search-mode-btn"
              :class="{ active: useBackendSearch }"
              :title="
                useBackendSearch
                  ? 'Backend search (searches all rows)'
                  : 'Local filter (searches loaded rows only)'
              "
            >
              <i
                :class="useBackendSearch ? 'fas fa-database' : 'fas fa-filter'"
              ></i>
              {{ useBackendSearch ? 'All' : 'Local' }}
            </button>
            <button
              @click="fetchActivityLogs"
              :disabled="isLoadingLogs"
              class="log-action-btn refresh-logs-btn"
              title="Refresh activity logs"
            >
              <span v-if="isLoadingLogs" class="spinner"></span>
              <span v-else><i class="fas fa-sync-alt"></i></span>
            </button>
            <div class="log-actions">
              <button
                @click="deleteFilteredLogs"
                :disabled="filteredActivityLogs.length === 0 || isDeletingLogs"
                class="log-action-btn delete-btn"
                title="Delete filtered log entries"
              >
                <span v-if="isDeletingLogs" class="spinner"></span>
                {{ isDeletingLogs ? 'Deleting...' : 'Delete These' }}
              </button>
              <div class="trim-controls">
                <input
                  v-model.number="trimDays"
                  type="number"
                  min="1"
                  max="365"
                  class="trim-input"
                  title="Days to keep"
                />
                <button
                  @click="trimOldLogs"
                  :disabled="isTrimmingLogs"
                  class="log-action-btn trim-btn"
                  title="Remove logs older than specified days"
                >
                  <span v-if="isTrimmingLogs" class="spinner"></span>
                  {{ isTrimmingLogs ? 'Trimming...' : 'Trim Logs' }}
                </button>
              </div>
            </div>
            <div class="log-info">
              {{ filteredActivityLogs.length }} of
              {{ activityLogs.length }} entries
              <button
                @click="loadMoreLogs"
                class="load-more-btn"
                :disabled="isLoadingMore"
                title="Load 1000 more entries"
              >
                <span v-if="isLoadingMore" class="spinner-small"></span>
                {{ isLoadingMore ? '...' : '+1000' }}
              </button>
            </div>
          </div>
          <div class="activity-log-container">
            <div class="log-header">
              <div class="log-col-datetime">DateTime</div>
              <div class="log-col-user">User</div>
              <div class="log-col-method">Method</div>
              <div class="log-col-url">URL</div>
              <div class="log-col-ip">IP Address</div>
            </div>
            <div class="log-entries" ref="logEntries">
              <div v-if="activityLogError" class="error-message">
                <div class="error-icon">⚠️</div>
                <div class="error-text">{{ activityLogError }}</div>
              </div>
              <div v-else-if="isLoadingLogs" class="loading-logs">
                <span class="spinner"></span>
                Loading activity logs...
              </div>
              <div
                v-else-if="filteredActivityLogs.length === 0"
                class="no-logs"
              >
                <span v-if="activitySearchTerm"
                  >No logs match your search criteria</span
                >
                <span v-else>No activity logs found</span>
              </div>
              <div
                v-else
                v-for="log in filteredActivityLogs"
                :key="log.id"
                class="log-entry"
              >
                <div class="log-col-datetime" :title="log.access_datetime">
                  {{ formatDateTime(log.access_datetime) }}
                </div>
                <div class="log-col-user" :title="log.username">
                  <span v-html="highlightSearchTerm(log.username)"></span>
                </div>
                <div
                  class="log-col-method"
                  :class="getMethodClass(log.request_method)"
                >
                  {{ log.request_method }}
                </div>
                <div class="log-col-url" :title="log.request_url">
                  <span v-html="highlightSearchTerm(log.request_url)"></span>
                </div>
                <div class="log-col-ip" :title="log.ip_address">
                  <span
                    v-html="highlightSearchTerm(log.ip_address || 'N/A')"
                  ></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';

export default {
  name: 'ServerManagement',
  data() {
    return {
      isRefreshing: false,
      isBlacklisting: false,
      isForcingReset: false,
      usernameToBlacklist: '',
      usernameToReset: '',
      lastOperationResult: null,
      operationDetails: [],
      showHelp: false,
      logTab: 'activity',
      // Activity Log data
      activityLogs: [],
      filteredActivityLogs: [],
      activitySearchTerm: '',
      useBackendSearch: false,
      isLoadingLogs: false,
      isLoadingMore: false,
      logLimit: 500,
      activityLogError: null,
      isDeletingLogs: false,
      isTrimmingLogs: false,
      trimDays: 7,
      successTimer: null,
      // Login analysis data
      loginAnalysis: null,
      loginAnalysisDays: 30,
      isLoadingAnalysis: false,
      loginAnalysisError: null,
      // Users tab
      adminUsers: [],
      userSearchTerm: '',
      isLoadingUsers: false,
      usersError: null,
      resettingUsername: null,
      extendingUsername: null,
      // Default: username ascending (matches API order)
      userSortField: 'username',
      userSortDir: 'asc',
    };
  },
  computed: {
    filteredAdminUsers() {
      const q = (this.userSearchTerm || '').trim().toLowerCase();
      let list = this.adminUsers;
      if (q) {
        list = list.filter((u) => {
          const hay = [
            u.username,
            u.displayName,
            u.phone,
            String(u.id),
            String(u.role),
            u.accountStatus,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return hay.includes(q);
        });
      }

      const field = this.userSortField;
      const dir = this.userSortDir === 'desc' ? -1 : 1;
      return list.slice().sort((a, b) => {
        if (field === 'lastAccessed') {
          const ta = a.lastAccessed ? new Date(a.lastAccessed).getTime() : 0;
          const tb = b.lastAccessed ? new Date(b.lastAccessed).getTime() : 0;
          if (ta !== tb) return (ta - tb) * dir;
          // stable tie-break by username
          return (a.username || '').localeCompare(b.username || '', undefined, {
            sensitivity: 'base',
          });
        }
        // default / username
        return (
          (a.username || '').localeCompare(b.username || '', undefined, {
            sensitivity: 'base',
          }) * dir
        );
      });
    },
    formatOperationDetailsLine() {
      if (!this.operationDetails?.length) return '';
      return this.operationDetails
        .map((d) => {
          let s = d.action;
          if (d.count !== undefined) s += ` (${d.count})`;
          if (!d.success && d.error) s += `: ${d.error}`;
          return s;
        })
        .join(' · ');
    },
  },
  methods: {
    clearOperationStatus() {
      if (this.successTimer) {
        clearTimeout(this.successTimer);
        this.successTimer = null;
      }
      this.lastOperationResult = null;
      this.operationDetails = [];
    },

    scheduleStatusClear(ms = 8000) {
      if (this.successTimer) {
        clearTimeout(this.successTimer);
      }
      this.successTimer = setTimeout(() => {
        this.lastOperationResult = null;
        this.operationDetails = [];
        this.successTimer = null;
      }, ms);
    },
    async blacklistUserTokens() {
      if (!this.usernameToBlacklist.trim()) {
        this.showError('Please enter a username or user ID');
        return;
      }

      const identifier = this.usernameToBlacklist.trim();

      // Check for special "all users" command
      if (identifier === '{ all }') {
        await this.blacklistAllUsers();
        return;
      }

      this.isBlacklisting = true;
      this.lastOperationResult = null;
      this.operationDetails = [];

      try {
        // Call the blacklist API
        const response = await axios.post('/api/blacklist-user-tokens', {
          identifier: identifier,
        });

        // Show success message
        this.lastOperationResult = {
          success: true,
          message:
            response.data.message || 'User tokens blacklisted successfully',
          timestamp: new Date().toLocaleString(),
        };

        // Add operation details
        this.operationDetails = [
          {
            action: 'User tokens blacklisted',
            success: true,
            count: response.data.tokensBlacklisted || 0,
          },
          { action: 'Database updated', success: true },
        ];

        // Clear the input
        this.usernameToBlacklist = '';

        // Automatically refresh server cache after blacklisting
        await this.refreshServerAfterBlacklist();
        this.scheduleStatusClear(8000);

        // Show success notification
        this.$emit('blacklist-success', response.data);
      } catch (error) {
        console.error('Token blacklisting failed:', error);

        this.lastOperationResult = {
          success: false,
          message:
            error.response?.data?.error ||
            error.message ||
            'Token blacklisting failed',
          timestamp: new Date().toLocaleString(),
        };
        this.operationDetails = [];
        this.scheduleStatusClear(10000);

        // Show error notification
        this.$emit('blacklist-error', error);
      } finally {
        this.isBlacklisting = false;
      }
    },

    async blacklistAllUsers() {
      // Confirm the action with the user
      const confirmed = confirm(
        'Are you sure you want to blacklist ALL user tokens? This will log out EVERYONE (including yourself) and cannot be undone.',
      );

      if (!confirmed) {
        return;
      }

      this.isBlacklisting = true;
      this.lastOperationResult = null;
      this.operationDetails = [];

      try {
        // First, get all users
        const usersResponse = await axios.get('/api/users');
        const users = usersResponse.data || [];

        if (users.length === 0) {
          this.showError('No users found to blacklist');
          return;
        }

        this.operationDetails.push({
          action: `Found ${users.length} users to blacklist`,
          success: true,
          count: users.length,
        });

        let successCount = 0;
        let errorCount = 0;
        let totalTokensBlacklisted = 0;

        // Process each user
        for (const user of users) {
          try {
            const response = await axios.post('/api/blacklist-user-tokens', {
              identifier: user.id.toString(),
            });

            successCount++;
            totalTokensBlacklisted += response.data.tokensBlacklisted || 0;

            this.operationDetails.push({
              action: `Blacklisted tokens for ${user.displayName}`,
              success: true,
              count: response.data.tokensBlacklisted || 0,
            });
          } catch (error) {
            errorCount++;
            this.operationDetails.push({
              action: `Failed to blacklist tokens for ${user.displayName}`,
              success: false,
              error: error.response?.data?.error || error.message,
            });
          }
        }

        // Final refresh of server cache
        await this.refreshServerAfterBlacklist();

        // Show final results
        this.lastOperationResult = {
          success: errorCount === 0,
          message: `Blacklisted tokens for ${successCount} users. ${totalTokensBlacklisted} total tokens blacklisted. ${errorCount} errors.`,
          timestamp: new Date().toLocaleString(),
        };
        this.scheduleStatusClear(8000);

        // Clear the input
        this.usernameToBlacklist = '';

        // Show success notification
        this.$emit('blacklist-success', {
          message: 'All user tokens blacklisted successfully',
          usersProcessed: successCount,
          totalTokensBlacklisted: totalTokensBlacklisted,
        });
      } catch (error) {
        console.error('Blacklist all users failed:', error);

        this.lastOperationResult = {
          success: false,
          message:
            error.response?.data?.error ||
            error.message ||
            'Failed to blacklist all user tokens',
          timestamp: new Date().toLocaleString(),
        };
        this.operationDetails = [];
        this.scheduleStatusClear(10000);

        // Show error notification
        this.$emit('blacklist-error', error);
      } finally {
        this.isBlacklisting = false;
      }
    },

    async fetchAdminUsers() {
      this.isLoadingUsers = true;
      this.usersError = null;
      try {
        const response = await axios.get('/api/admin/users');
        this.adminUsers = response.data?.users || [];
      } catch (error) {
        console.error('Failed to load users:', error);
        this.usersError =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          'Failed to load users';
        this.adminUsers = [];
      } finally {
        this.isLoadingUsers = false;
      }
    },

    /** Default view: no filter, username A→Z */
    resetUserTableView() {
      this.userSearchTerm = '';
      this.userSortField = 'username';
      this.userSortDir = 'asc';
    },

    toggleUserSortLastAccessed() {
      if (this.userSortField === 'lastAccessed') {
        this.userSortDir = this.userSortDir === 'desc' ? 'asc' : 'desc';
      } else {
        this.userSortField = 'lastAccessed';
        this.userSortDir = 'desc'; // most recent first
      }
    },

    handleUsersTabKeydown(event) {
      if (event.key !== 'Escape' || this.logTab !== 'users') return;
      const el = event.target;
      // Don't steal Esc from top-of-page action forms
      if (
        el &&
        el.closest &&
        !el.closest('.users-section') &&
        (el.tagName === 'INPUT' ||
          el.tagName === 'TEXTAREA' ||
          el.isContentEditable)
      ) {
        return;
      }
      event.preventDefault();
      this.resetUserTableView();
    },

    formatUserDate(value) {
      if (!value) return '—';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },

    formatUserDateTime(value) {
      if (!value) return '—';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    },

    lastAccessedTitle(user) {
      if (!user) return '';
      const parts = [];
      if (user.lastActivityAt) {
        parts.push(
          `Last API activity: ${this.formatUserDateTime(user.lastActivityAt)}`,
        );
      }
      if (user.lastLoginAt) {
        parts.push(`Last login: ${this.formatUserDateTime(user.lastLoginAt)}`);
      }
      if (user.lastTokenAt) {
        parts.push(`Last token: ${this.formatUserDateTime(user.lastTokenAt)}`);
      }
      return parts.join('\n') || 'No access recorded';
    },

    formatDaysUntil(days) {
      if (days == null) return '';
      if (days > 0) return `${days}d left`;
      if (days === 0) return 'today';
      return `${Math.abs(days)}d past`;
    },

    formatAccountStatus(status) {
      const map = {
        active: 'Active',
        grace: 'Grace period',
        expired: 'Expired',
        reset_required: 'Reset required',
        no_expiry: 'No expiry',
      };
      return map[status] || status || '—';
    },

    async forcePasswordResetForUser(user) {
      if (!user?.username) return;
      const confirmed = confirm(
        `Require password reset for "${user.username}"?\n\nThey will be logged out and must set a new password on next login.`,
      );
      if (!confirmed) return;
      this.resettingUsername = user.username;
      this.usernameToReset = user.username;
      try {
        await this.forcePasswordReset();
        await this.fetchAdminUsers();
      } finally {
        this.resettingUsername = null;
      }
    },

    async extendUserExpiry(user) {
      if (!user?.username && !user?.id) return;
      const days = 180;
      const current = user.expires
        ? this.formatUserDate(user.expires)
        : 'none';
      const confirmed = confirm(
        `Extend account expiry for "${user.username}" by ${days} days?\n\nCurrent expiry: ${current}\nNew expiry will be ${days} days after the later of today or the current expiry.`,
      );
      if (!confirmed) return;

      this.extendingUsername = user.username;
      this.clearOperationStatus();
      try {
        const response = await axios.post('/api/admin/extend-user-expiry', {
          userId: user.id,
          username: user.username,
          days,
        });
        const msg =
          response.data?.message ||
          `Extended expiry for ${user.username} by ${days} days`;
        const details = response.data?.expires
          ? [
              {
                action: `New expiry: ${this.formatUserDate(response.data.expires)}`,
                success: true,
              },
            ]
          : [];
        this.showSuccessMessage(msg, details);
        await this.fetchAdminUsers();
      } catch (error) {
        console.error('Extend user expiry failed:', error);
        this.lastOperationResult = {
          success: false,
          message:
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Failed to extend expiry',
          timestamp: new Date().toLocaleString(),
        };
        this.operationDetails = [];
        this.scheduleStatusClear(10000);
      } finally {
        this.extendingUsername = null;
      }
    },

    async forcePasswordReset() {
      if (!this.usernameToReset.trim()) {
        this.showError('Please enter a username');
        return;
      }

      this.isForcingReset = true;
      this.lastOperationResult = null;
      this.operationDetails = [];

      try {
        const username = this.usernameToReset.trim();

        // First, call the force password change API
        const resetResponse = await axios.post('/api/force-password-change', {
          username: username,
        });

        this.operationDetails.push({
          action: 'Password reset flag set',
          success: true,
        });

        // Then blacklist the user's current tokens for immediate logout
        try {
          const blacklistResponse = await axios.post(
            '/api/blacklist-user-tokens',
            {
              identifier: username,
            },
          );

          this.operationDetails.push({
            action: `Blacklisted ${blacklistResponse.data.tokensBlacklisted || 0} active tokens`,
            success: true,
            count: blacklistResponse.data.tokensBlacklisted || 0,
          });
        } catch (blacklistError) {
          console.warn(
            'Token blacklisting failed during password reset:',
            blacklistError,
          );
          this.operationDetails.push({
            action: 'Token blacklisting failed',
            success: false,
            error:
              blacklistError.response?.data?.error || blacklistError.message,
          });
        }

        // Refresh server cache after operations
        await this.refreshServerAfterBlacklist();

        // Show success message
        this.lastOperationResult = {
          success: true,
          message: 'Password reset required and user logged out successfully',
          timestamp: new Date().toLocaleString(),
        };
        this.scheduleStatusClear(8000);

        // Clear the input
        this.usernameToReset = '';

        // Show success notification
        this.$emit('reset-success', resetResponse.data);
      } catch (error) {
        console.error('Force password reset failed:', error);

        this.lastOperationResult = {
          success: false,
          message:
            error.response?.data?.error ||
            error.message ||
            'Force password reset failed',
          timestamp: new Date().toLocaleString(),
        };
        this.operationDetails = [];
        this.scheduleStatusClear(10000);

        // Show error notification
        this.$emit('reset-error', error);
      } finally {
        this.isForcingReset = false;
      }
    },

    async refreshServerAfterBlacklist() {
      try {
        // Add a small delay to ensure database changes are committed
        await new Promise((resolve) => setTimeout(resolve, 500));

        const response = await axios.post('/api/refresh-server');

        // Add refresh details to the existing operation details
        this.operationDetails.push(
          {
            action: 'Server cache refreshed',
            success: true,
            count: response.data.details?.[0]?.count || 0,
          },
          { action: 'Changes applied immediately', success: true },
        );

        console.log('Server cache refreshed automatically after blacklisting');
      } catch (error) {
        console.error('Auto-refresh failed:', error);
        this.operationDetails.push({
          action: 'Server cache refresh',
          success: false,
        });
      }
    },

    async refreshServer() {
      this.isRefreshing = true;
      this.lastOperationResult = null;
      this.operationDetails = [];

      try {
        const response = await axios.post('/api/refresh-server');

        this.lastOperationResult = {
          success: true,
          message:
            response.data.message || 'Server refresh completed successfully',
          timestamp: new Date().toLocaleString(),
        };

        // Parse the details if provided
        if (response.data.details && Array.isArray(response.data.details)) {
          this.operationDetails = response.data.details;
        }
        this.scheduleStatusClear(8000);

        // Show success notification
        this.$emit('refresh-success', response.data);
      } catch (error) {
        console.error('Server refresh failed:', error);

        this.lastOperationResult = {
          success: false,
          message:
            error.response?.data?.error ||
            error.message ||
            'Server refresh failed',
          timestamp: new Date().toLocaleString(),
        };
        this.operationDetails = [];
        this.scheduleStatusClear(10000);

        // Show error notification
        this.$emit('refresh-error', error);
      } finally {
        this.isRefreshing = false;
      }
    },

    showError(message) {
      this.lastOperationResult = {
        success: false,
        message: message,
        timestamp: new Date().toLocaleString(),
      };
      this.operationDetails = [];
      this.scheduleStatusClear(10000);
    },

    // Show success message with auto-hide
    showSuccessMessage(message, details = []) {
      this.operationDetails = details || [];
      this.lastOperationResult = {
        success: true,
        message: message,
        timestamp: new Date().toLocaleString(),
      };
      this.scheduleStatusClear(8000);
    },

    // Public method that can be called from parent components
    triggerRefresh() {
      return this.refreshServer();
    },

    // Toggle help section visibility
    toggleHelp() {
      this.showHelp = !this.showHelp;
    },

    // Clear search input
    clearSearch() {
      this.activitySearchTerm = '';
      if (this.useBackendSearch) {
        this.fetchActivityLogs();
      } else {
        this.filterActivityLogs();
      }
    },

    // Toggle between local and backend search
    toggleSearchMode() {
      this.useBackendSearch = !this.useBackendSearch;
      // Clear search when switching modes
      if (this.activitySearchTerm) {
        this.activitySearchTerm = '';
        this.fetchActivityLogs();
      }
    },

    // Backend search method
    async searchBackend() {
      if (!this.activitySearchTerm.trim()) {
        this.fetchActivityLogs();
        return;
      }

      this.isLoadingLogs = true;
      this.activityLogError = null;

      try {
        // Split by comma - first term goes to backend, rest are local filters
        const searchTerms = this.activitySearchTerm
          .split(',')
          .map((t) => t.trim())
          .filter((t) => t.length > 0);

        const backendTerm = searchTerms[0] || '';
        const localFilters = searchTerms.slice(1);

        const isInverted = backendTerm.startsWith('!');
        const searchTerm = isInverted ? backendTerm.slice(1) : backendTerm;

        const params = {
          limit: this.logLimit,
          offset: 0,
        };

        if (searchTerm) {
          if (isInverted) {
            params.excludeSearch = searchTerm;
          } else {
            params.search = searchTerm;
          }
        }

        const response = await axios.get('/api/activity-logs', { params });

        if (response.data && response.data.success) {
          this.activityLogs = response.data.logs || [];

          // Apply local filters for comma-separated terms after the first
          if (localFilters.length > 0) {
            this.filteredActivityLogs = this.activityLogs.filter((log) => {
              const logText = [
                log.username,
                log.request_url,
                log.request_method,
                log.ip_address,
              ]
                .join(' ')
                .toLowerCase();

              return localFilters.every((filter) => {
                const isExclude = filter.startsWith('!');
                const term = (
                  isExclude ? filter.slice(1) : filter
                ).toLowerCase();
                const matches = logText.includes(term);
                return isExclude ? !matches : matches;
              });
            });
          } else {
            this.filteredActivityLogs = [...this.activityLogs];
          }
        } else {
          this.activityLogs = [];
          this.filteredActivityLogs = [];
          this.activityLogError = 'Failed to search activity logs';
        }
      } catch (error) {
        console.error('Error searching activity logs:', error);
        this.activityLogs = [];
        this.filteredActivityLogs = [];
        this.activityLogError = 'Failed to search activity logs';
      } finally {
        this.isLoadingLogs = false;
      }
    },

    // Activity log methods
    async fetchActivityLogs() {
      this.isLoadingLogs = true;
      this.activityLogError = null;
      try {
        const response = await axios.get('/api/activity-logs', {
          params: {
            limit: this.logLimit,
            offset: 0,
          },
        });

        if (response.data && response.data.success) {
          this.activityLogs = response.data.logs || [];
          this.filterActivityLogs();
        } else {
          console.error('Failed to fetch activity logs:', response.data);
          this.activityLogs = [];
          this.filteredActivityLogs = [];
          this.activityLogError = 'Failed to load activity logs';
        }
      } catch (error) {
        console.error('Error fetching activity logs:', error);
        this.activityLogs = [];
        this.filteredActivityLogs = [];

        // Handle different error types
        if (error.response?.status === 403) {
          this.activityLogError = 'Not authorized to view activity logs';
        } else {
          this.activityLogError = 'Failed to load activity logs';
        }
      } finally {
        this.isLoadingLogs = false;
      }
    },

    async loadMoreLogs() {
      this.isLoadingMore = true;
      this.logLimit += 1000;
      try {
        const response = await axios.get('/api/activity-logs', {
          params: {
            limit: this.logLimit,
            offset: 0,
          },
        });

        if (response.data && response.data.success) {
          this.activityLogs = response.data.logs || [];
          this.filterActivityLogs();
        }
      } catch (error) {
        console.error('Error loading more logs:', error);
      } finally {
        this.isLoadingMore = false;
      }
    },

    filterActivityLogs() {
      if (!this.activitySearchTerm.trim()) {
        this.filteredActivityLogs = [...this.activityLogs];
        return;
      }

      const rawSearchTerm = this.activitySearchTerm.trim();
      const isInverted = rawSearchTerm.startsWith('!');
      const searchTerm = (
        isInverted ? rawSearchTerm.slice(1) : rawSearchTerm
      ).toLowerCase();

      if (!searchTerm) {
        this.filteredActivityLogs = [...this.activityLogs];
        return;
      }

      this.filteredActivityLogs = this.activityLogs.filter((log) => {
        const matchFound =
          log.username.toLowerCase().includes(searchTerm) ||
          log.request_url.toLowerCase().includes(searchTerm) ||
          log.request_method.toLowerCase().includes(searchTerm) ||
          (log.ip_address &&
            log.ip_address.toLowerCase().includes(searchTerm)) ||
          log.access_datetime.toLowerCase().includes(searchTerm);

        // If inverted search (!), exclude matches. Otherwise, include matches.
        return isInverted ? !matchFound : matchFound;
      });
    },

    highlightSearchTerm(text) {
      if (!this.activitySearchTerm.trim() || !text) {
        return text || '';
      }

      const rawSearchTerm = this.activitySearchTerm.trim();
      const isInverted = rawSearchTerm.startsWith('!');
      const searchTerm = (
        isInverted ? rawSearchTerm.slice(1) : rawSearchTerm
      ).trim();

      // Don't highlight for inverted searches or empty search terms
      if (isInverted || !searchTerm) {
        return text || '';
      }

      const regex = new RegExp(`(${this.escapeRegex(searchTerm)})`, 'gi');
      return text.replace(regex, '<mark class="search-highlight">$1</mark>');
    },

    escapeRegex(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return 'N/A';
      try {
        const date = new Date(dateTimeString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
      } catch (error) {
        return dateTimeString;
      }
    },

    getMethodClass(method) {
      const methodClasses = {
        GET: 'method-get',
        POST: 'method-post',
        PUT: 'method-put',
        DELETE: 'method-delete',
        PATCH: 'method-patch',
      };
      return methodClasses[method] || 'method-other';
    },

    async deleteFilteredLogs() {
      if (this.filteredActivityLogs.length === 0) {
        return;
      }

      const confirmed = confirm(
        `Are you sure you want to delete ${this.filteredActivityLogs.length} log entries? This action cannot be undone.`,
      );

      if (!confirmed) {
        return;
      }

      this.isDeletingLogs = true;

      try {
        const logIds = this.filteredActivityLogs.map((log) => log.id);

        const response = await axios.delete('/api/activity-logs', {
          data: { logIds },
        });

        if (response.data && response.data.success) {
          // Remove deleted logs from local arrays
          this.activityLogs = this.activityLogs.filter(
            (log) => !logIds.includes(log.id),
          );
          this.filteredActivityLogs = this.filteredActivityLogs.filter(
            (log) => !logIds.includes(log.id),
          );

          // Show success message
          this.showSuccessMessage(
            `Successfully deleted ${logIds.length} log entries`,
          );
        } else {
          throw new Error(response.data?.message || 'Failed to delete logs');
        }
      } catch (error) {
        console.error('Error deleting logs:', error);
        this.lastOperationResult = {
          success: false,
          message:
            error.response?.data?.error ||
            error.message ||
            'Failed to delete logs',
          timestamp: new Date().toLocaleString(),
        };
      } finally {
        this.isDeletingLogs = false;
      }
    },

    async trimOldLogs() {
      if (!this.trimDays || this.trimDays < 1) {
        this.lastOperationResult = {
          success: false,
          message: 'Please enter a valid number of days (1 or greater)',
          timestamp: new Date().toLocaleString(),
        };
        return;
      }

      const confirmed = confirm(
        `Are you sure you want to delete all log entries older than ${this.trimDays} days? This action cannot be undone.`,
      );

      if (!confirmed) {
        return;
      }

      this.isTrimmingLogs = true;

      try {
        const response = await axios.delete('/api/activity-logs/trim', {
          data: { days: this.trimDays },
        });

        if (response.data && response.data.success) {
          // Refresh the logs to show updated data
          await this.fetchActivityLogs();

          // Show success message
          this.showSuccessMessage(
            `Successfully deleted ${response.data.deletedCount || 0} log entries older than ${this.trimDays} days`,
          );
        } else {
          throw new Error(response.data?.message || 'Failed to trim logs');
        }
      } catch (error) {
        console.error('Error trimming logs:', error);
        this.lastOperationResult = {
          success: false,
          message:
            error.response?.data?.error ||
            error.message ||
            'Failed to trim logs',
          timestamp: new Date().toLocaleString(),
        };
      } finally {
        this.isTrimmingLogs = false;
      }
    },

    // Login analysis methods
    async fetchLoginAnalysis() {
      this.isLoadingAnalysis = true;
      this.loginAnalysisError = null;
      try {
        const response = await axios.get('/api/login-history/analysis', {
          params: { days: this.loginAnalysisDays },
        });
        if (response.data && response.data.success) {
          this.loginAnalysis = response.data.analysis;
        } else {
          this.loginAnalysisError = 'Failed to load analysis';
        }
      } catch (error) {
        console.error('Error fetching login analysis:', error);
        this.loginAnalysisError =
          error.response?.status === 403
            ? 'Not authorized to view login analysis'
            : 'Failed to load login analysis';
      } finally {
        this.isLoadingAnalysis = false;
      }
    },

    formatTime(isoStr) {
      if (!isoStr) return '';
      try {
        return new Date(isoStr).toLocaleTimeString();
      } catch {
        return isoStr;
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      try {
        return new Date(dateStr).toLocaleDateString();
      } catch {
        return dateStr;
      }
    },

    truncate(str, max) {
      if (!str) return '';
      return str.length > max ? str.substring(0, max) + '...' : str;
    },

    getFailRate(row) {
      const total = (row.failed || 0) + (row.success || 0);
      if (total === 0) return 0;
      return Math.round(((row.failed || 0) / total) * 100);
    },
  },
  mounted() {
    // Set the document title
    document.title = 'Server Management';

    // Fetch activity logs when component is mounted
    this.fetchActivityLogs();
    document.addEventListener('keydown', this.handleUsersTabKeydown);
  },
  beforeUnmount() {
    // Clear any active success timer when component is destroyed
    if (this.successTimer) {
      clearTimeout(this.successTimer);
      this.successTimer = null;
    }
    document.removeEventListener('keydown', this.handleUsersTabKeydown);
  },
  emits: [
    'refresh-success',
    'refresh-error',
    'blacklist-success',
    'blacklist-error',
    'reset-success',
    'reset-error',
  ],
};
</script>

<style scoped>
/* Fill app-view (body is overflow:hidden) and scroll inside this panel */
.server-management-container {
  max-width: 1000px;
  width: 100%;
  margin: 0 auto;
  padding: 15px;
  box-sizing: border-box;
  flex: 1;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  touch-action: pan-y;
  -webkit-overflow-scrolling: touch;
}

.card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.card-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 15px;
  text-align: center;
  flex-shrink: 0;
}

.card-header h3 {
  margin: 0;
  font-size: 1.4em;
  font-weight: 600;
}

.subtitle {
  margin: 5px 0 0 0;
  opacity: 0.9;
  font-size: 0.85em;
}

.card-body {
  padding: 15px;
  display: grid;
  /* minmax(0, 1fr) prevents wide children (e.g. users table) from blowing up columns */
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 15px;
  max-width: 100%;
  min-width: 0;
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  align-content: start;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
}

.action-section {
  text-align: left;
  padding: 15px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fafafa;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.action-section h4 {
  margin: 0 0 10px 0;
  color: #333;
  font-size: 1.1em;
  text-align: left;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.section-header h4 {
  margin: 0;
  color: #333;
  font-size: 1.1em;
}

.help-icon {
  color: #667eea;
  cursor: pointer;
  font-size: 1.1em;
  transition: color 0.3s ease;
}

.help-icon:hover {
  color: #5a6fd8;
}

.blacklist-form,
.password-reset-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.form-group {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.form-group label {
  margin-bottom: 3px;
  font-weight: 600;
  color: #333;
  font-size: 0.85em;
}

.username-input {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 2px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.3s ease;
}

.username-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.username-input:disabled {
  background: #f5f5f5;
  color: #999;
}

.blacklist-btn,
.reset-btn,
.refresh-btn {
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.blacklist-btn {
  background: #dc3545;
}

.blacklist-btn:hover:not(:disabled) {
  background: #c82333;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
}

.reset-btn {
  background: #ffc107;
  color: #333;
}

.reset-btn:hover:not(:disabled) {
  background: #e0a800;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.3);
}

.refresh-btn {
  background: #4caf50;
}

.refresh-btn:hover:not(:disabled) {
  background: #45a049;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
}

.blacklist-btn:disabled,
.reset-btn:disabled,
.refresh-btn:disabled {
  background: #cccccc;
  cursor: not-allowed;
  transform: none;
  color: #666;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s linear infinite;
  margin-right: 8px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.help-text {
  margin: 5px 0 0 0;
  color: #666;
  font-size: 0.8em;
  line-height: 1.3;
}

.status-section {
  margin-bottom: 8px;
  grid-column: 1 / -1;
  position: sticky;
  top: 0;
  z-index: 6;
}

.status-message {
  display: flex;
  align-items: flex-start;
  padding: 8px 10px;
  border-radius: 4px;
  margin-bottom: 0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.status-message.success {
  background: #f0f9f0;
  border-left: 4px solid #4caf50;
}

.status-message.error {
  background: #fdf2f2;
  border-left: 4px solid #f56565;
}

.status-icon {
  font-size: 1.2em;
  margin-right: 12px;
  flex-shrink: 0;
}

.status-content {
  flex: 1;
}

.status-content strong {
  display: block;
  margin-bottom: 2px;
  font-size: 0.9em;
}

.status-content p {
  margin: 0;
  color: #666;
  font-size: 0.85em;
  line-height: 1.3;
}

.status-detail-line {
  display: block;
  margin-top: 2px;
  color: #888;
  font-size: 0.75em;
  line-height: 1.25;
}

.status-dismiss {
  flex-shrink: 0;
  margin-left: 8px;
  border: none;
  background: transparent;
  color: #888;
  font-size: 1.25em;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
}

.status-dismiss:hover {
  color: #333;
}

.detail-count {
  color: #666;
  font-size: 0.9em;
}

.help-section {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 4px;
  border-left: 4px solid #17a2b8;
  grid-column: 1 / -1;
}

.help-section h4 {
  margin: 0 0 8px 0;
  color: #333;
  font-size: 0.95em;
}

.help-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.help-header h4 {
  margin: 0;
  color: #333;
  font-size: 0.95em;
}

.close-hint {
  color: #666;
  font-size: 0.8em;
  cursor: pointer;
  font-style: italic;
  transition: color 0.3s ease;
}

.close-hint:hover {
  color: #333;
}

.help-list {
  margin: 0;
  padding-left: 16px;
}

.help-list li {
  margin-bottom: 4px;
  color: #666;
  font-size: 0.8em;
  line-height: 1.4;
}

.help-list strong {
  color: #333;
}

.help-list code {
  background: #e9ecef;
  padding: 2px 5px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 0.85em;
}

/* Activity Log Styles */
.activity-log-section {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  grid-column: 1 / -1;
  margin-bottom: 10px;
}

.activity-log-section h4 {
  margin: 0 0 12px 0;
  color: #333;
  font-size: 1.1em;
}

.log-search-wrapper {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 12px;
}

.search-input-container {
  flex: 1;
  display: flex;
  align-items: center;
  position: relative;
}

.search-mode-btn {
  padding: 4px 10px;
  border: 2px solid #ddd;
  border-radius: 4px;
  background: #f5f5f5;
  color: #666;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.search-mode-btn:hover {
  border-color: #667eea;
  color: #667eea;
}

.search-mode-btn.active {
  background: #667eea;
  border-color: #667eea;
  color: white;
}

.search-mode-btn.active:hover {
  background: #5a6fd6;
  border-color: #5a6fd6;
}

.log-search-input {
  flex: 1;
  padding: 4px 12px;
  padding-right: 35px;
  border: 2px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.3s ease;
}

.clear-search-btn {
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  font-size: 16px;
  font-weight: bold;
  transition: all 0.3s ease;
  z-index: 1;
}

.clear-search-btn:hover {
  background: #f0f0f0;
  color: #333;
}

.log-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.log-action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.delete-btn {
  background: #a080a8;
  color: white;
}

.delete-btn:hover:not(:disabled) {
  background: #8a6d8d;
  transform: translateY(-1px);
}

.delete-btn:disabled {
  background: #cccccc;
  cursor: not-allowed;
  transform: none;
}

.trim-controls {
  display: flex;
  align-items: center;
  gap: 5px;
}

.trim-input {
  width: 50px;
  padding: 4px 6px;
  border: 1px solid #ddd;
  border-radius: 3px;
  font-size: 12px;
  text-align: center;
}

.trim-btn {
  background: #a080a8;
  color: white;
}

.trim-btn:hover:not(:disabled) {
  background: #8a6d8d;
  transform: translateY(-1px);
}

.trim-btn:disabled {
  background: #cccccc;
  cursor: not-allowed;
  transform: none;
  color: #666;
}

.refresh-logs-btn {
  background: #a080a8;
  color: white;
}

.refresh-logs-btn:hover:not(:disabled) {
  background: #8a6d8d;
  transform: translateY(-1px);
}

.refresh-logs-btn:disabled {
  background: #cccccc;
  cursor: not-allowed;
  transform: none;
  color: #666;
}

.log-search-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.log-info {
  color: #666;
  font-size: 0.85em;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;
}

.load-more-btn {
  background: #667eea;
  color: white;
  border: none;
  border-radius: 3px;
  padding: 2px 6px;
  font-size: 0.85em;
  cursor: pointer;
  transition: background 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.load-more-btn:hover:not(:disabled) {
  background: #5a6fd6;
}

.load-more-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner-small {
  width: 10px;
  height: 10px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.activity-log-container {
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}

.log-header {
  display: grid;
  grid-template-columns: 130px 100px 70px 1fr 110px;
  background: #f1f3f4;
  border-bottom: 2px solid #ddd;
  font-weight: 600;
  font-size: 0.8em;
  color: #333;
}

.log-header > div {
  padding: 8px 6px;
  border-right: 1px solid #ddd;
}

.log-header > div:last-child {
  border-right: none;
}

.log-entries {
  max-height: 250px;
  overflow-y: auto;
  /* Hide scrollbar for Firefox */
  scrollbar-width: none;
  /* Hide scrollbar for IE/Edge */
  -ms-overflow-style: none;
}

/* Ensure scrollbar is completely hidden */
.log-entries::-webkit-scrollbar {
  display: none;
}

.log-entry {
  display: grid;
  grid-template-columns: 130px 100px 70px 1fr 110px;
  border-bottom: 1px solid #eee;
  font-size: 0.75em;
  transition: background-color 0.2s ease;
}

.log-entry:hover {
  background-color: #f8f9fa;
}

.log-entry:nth-child(even) {
  background-color: #fafafa;
}

.log-entry:nth-child(even):hover {
  background-color: #f0f0f0;
}

.log-col-datetime,
.log-col-user,
.log-col-method,
.log-col-url,
.log-col-ip {
  padding: 6px 4px;
  border-right: 1px solid #eee;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.log-col-datetime,
.log-col-user,
.log-col-method,
.log-col-url,
.log-col-ip:last-child {
  border-right: none;
}

.log-col-datetime {
  color: #666;
  font-size: 0.8em;
}

.log-col-user {
  font-weight: 500;
  color: #333;
}

.log-col-method {
  font-weight: 600;
  text-align: center;
  font-size: 0.8em;
}

.method-get {
  color: #28a745;
}

.method-post {
  color: #007bff;
}

.method-put {
  color: #ffc107;
}

.method-delete {
  color: #dc3545;
}

.method-patch {
  color: #6f42c1;
}

.method-other {
  color: #6c757d;
}

.log-col-url {
  font-family: 'Courier New', monospace;
  color: #495057;
}

.log-col-ip {
  color: #6c757d;
  font-family: 'Courier New', monospace;
  font-size: 0.8em;
}

.no-logs {
  padding: 20px;
  text-align: center;
  color: #666;
  font-style: italic;
}

.loading-logs {
  padding: 20px;
  text-align: center;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.error-message {
  padding: 20px;
  text-align: center;
  background: #fdf2f2;
  border: 1px solid #f56565;
  border-radius: 4px;
  margin: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.error-icon {
  font-size: 1.2em;
  color: #e53e3e;
}

.error-text {
  color: #c53030;
  font-weight: 500;
}

.search-highlight {
  background-color: #fff3cd;
  color: #856404;
  padding: 1px 2px;
  border-radius: 2px;
  font-weight: 600;
}

/* Responsive design */
@media (max-width: 1024px) {
  .card-body {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .log-header,
  .log-entry {
    grid-template-columns: 90px 80px 60px 1fr 80px;
  }

  .log-col-datetime {
    font-size: 0.65em;
  }

  .log-col-user {
    font-size: 0.7em;
  }
}

@media (max-width: 768px) {
  .server-management-container {
    padding: 10px;
    max-width: 100%;
  }

  .card-body {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .action-section {
    padding: 10px;
  }

  .log-search-wrapper {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .search-input-container {
    width: 100%;
  }

  .log-actions {
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
  }

  .trim-controls {
    flex-direction: row;
    align-items: center;
  }

  .log-header,
  .log-entry {
    grid-template-columns: 70px 60px 50px 1fr;
  }

  .log-col-ip {
    display: none;
  }

  .log-entries {
    max-height: 250px;
  }

  .analysis-grid {
    grid-template-columns: 1fr !important;
  }
}

/* ── Log Tab Styles ── */
.log-tabs {
  grid-column: 1 / -1;
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e0e0e0;
  margin-top: 10px;
}

.log-tab {
  padding: 8px 20px;
  border: 1px solid #e0e0e0;
  border-bottom: none;
  border-radius: 6px 6px 0 0;
  background: #f5f5f5;
  color: #666;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition:
    background 0.2s,
    color 0.2s;
}

.log-tab:hover {
  background: #eee;
}

.log-tab.active {
  background: #fff;
  color: #333;
  border-color: #e0e0e0;
  margin-bottom: -2px;
  border-bottom: 2px solid #fff;
}

.log-tab i {
  margin-right: 6px;
}

/* ── Login Analysis Styles ── */
.login-analysis-section {
  grid-column: 1 / -1;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 15px;
  background: #fafafa;
}

.login-analysis-section .section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.login-analysis-section h4 {
  margin: 0;
  color: #333;
  font-size: 1.1em;
}

.analysis-controls {
  display: flex;
  gap: 8px;
  align-items: center;
}

.days-select {
  padding: 4px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.85em;
  background: white;
}

.analysis-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  max-height: 340px;
  overflow-y: auto;
}

.analysis-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 12px;
}

.analysis-card.full-width {
  grid-column: 1 / -1;
}

.analysis-card h5 {
  margin: 0 0 10px 0;
  font-size: 0.95em;
  color: #555;
  display: flex;
  align-items: center;
  gap: 6px;
}

.badge-count {
  background: #e53935;
  color: white;
  border-radius: 10px;
  padding: 1px 7px;
  font-size: 0.8em;
  font-weight: 600;
}

.summary-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.stat-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.stat-badge {
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 0.8em;
  font-weight: 600;
  text-transform: capitalize;
}

.stat-success {
  background: #e8f5e9;
  color: #2e7d32;
}

.stat-failed {
  background: #ffebee;
  color: #c62828;
}

.stat-password_reset_required,
.stat-password_reset_completed {
  background: #fff3e0;
  color: #e65100;
}

.stat-count {
  font-weight: 600;
  font-size: 0.95em;
}

.lockout-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.lockout-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.85em;
  background: #f5f5f5;
}

.lockout-row.active {
  background: #ffebee;
  border-left: 3px solid #e53935;
}

.lockout-ip {
  font-family: monospace;
  font-weight: 600;
}

.lockout-fails {
  color: #c62828;
  font-weight: 600;
}

.lockout-until {
  color: #777;
  font-size: 0.85em;
}

.lockouts-card {
  border-color: #ffcdd2;
}

.analysis-table-wrap {
  overflow-x: auto;
  max-height: 150px;
  overflow-y: auto;
}

.analysis-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85em;
}

.analysis-table th {
  background: #f5f5f5;
  padding: 6px 8px;
  text-align: left;
  font-weight: 600;
  color: #555;
  border-bottom: 2px solid #ddd;
  position: sticky;
  top: 0;
}

.analysis-table td {
  padding: 5px 8px;
  border-bottom: 1px solid #eee;
}

.analysis-table tbody tr:hover {
  background: #f8f8ff;
}

.ip-cell {
  font-family: monospace;
  font-size: 0.9em;
}

.count-cell {
  font-weight: 600;
  text-align: center;
}

.count-cell.high-count {
  color: #c62828;
}

.count-cell.success-count {
  color: #2e7d32;
}

.usernames-cell {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.date-cell {
  white-space: nowrap;
  color: #666;
  font-size: 0.9em;
}

.fail-bar-container {
  display: flex;
  align-items: center;
  gap: 6px;
}

.fail-bar {
  height: 8px;
  background: #ef9a9a;
  border-radius: 4px;
  min-width: 2px;
  transition: width 0.3s;
}

.fail-bar.high-rate {
  background: #e53935;
}

.fail-rate-text {
  font-size: 0.85em;
  color: #666;
  white-space: nowrap;
}

.no-data {
  color: #999;
  font-style: italic;
  text-align: center;
  padding: 12px;
  margin: 0;
}

.users-section {
  grid-column: 1 / -1;
  margin-top: 8px;
  padding: 12px;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}

.users-section .section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
}

.users-section h4 {
  margin: 0;
  color: #333;
  font-size: 1.1em;
}

.user-filter-input {
  min-width: 160px;
  max-width: 220px;
  padding: 4px 8px;
  font-size: 0.85em;
}

.users-table-wrap {
  max-height: min(420px, 50vh);
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
  overflow-y: auto;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-x pan-y;
}

.users-table {
  width: max-content;
  min-width: 100%;
  table-layout: auto;
}

.users-table th,
.users-table td {
  white-space: nowrap;
  font-size: 0.85em;
}

.users-table th.sortable-th {
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
}

.users-table th.sortable-th:hover {
  background: #ebebeb;
  color: #333;
}

.users-table th.sortable-th.sorted {
  color: #5a4a8a;
}

.users-table th.sortable-th .fas {
  margin-left: 4px;
  font-size: 0.85em;
  opacity: 0.85;
}

.users-table th.sortable-th .sort-idle {
  opacity: 0.35;
}

.users-table .you-row {
  background: #f3eef8;
}

.you-badge {
  margin-left: 6px;
  font-size: 0.7em;
  font-weight: 600;
  color: #6a1b9a;
  background: #e1bee7;
  padding: 1px 6px;
  border-radius: 8px;
  text-transform: uppercase;
}

.days-hint {
  color: #888;
  font-size: 0.85em;
  margin-left: 4px;
}

.user-status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.75em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.user-status-badge.status-active {
  background: #e8f5e9;
  color: #2e7d32;
}

.user-status-badge.status-grace {
  background: #fff3e0;
  color: #ef6c00;
}

.user-status-badge.status-expired {
  background: #ffebee;
  color: #c62828;
}

.user-status-badge.status-reset_required {
  background: #fff8e1;
  color: #f9a825;
}

.user-status-badge.status-no_expiry {
  background: #eceff1;
  color: #546e7a;
}

.actions-cell {
  text-align: center;
}

.user-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.user-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border: none;
  border-radius: 4px;
  font-size: 0.8em;
  cursor: pointer;
  white-space: nowrap;
}

.user-action-btn.warning {
  background: #ff9800;
  color: white;
}

.user-action-btn.warning:hover:not(:disabled) {
  background: #f57c00;
}

.user-action-btn.extend {
  background: #43a047;
  color: white;
}

.user-action-btn.extend:hover:not(:disabled) {
  background: #2e7d32;
}

.user-action-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>
